// API entry point for the real backend.

import axios from 'axios'
import { toast } from '../store/toastStore'

export const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'https://jaritex-api.thetasol.com/api',
  timeout: 10000,
})

api.interceptors.request.use((config) => {
  try {
    const raw = localStorage.getItem('jaritex-auth')
    if (raw) {
      const { state } = JSON.parse(raw)
      if (state?.token) config.headers.Authorization = `Bearer ${state.token}`
    }
  } catch (_) { }
  return config
})

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // 1. Handle 401 Unauthorized
    if (err.response?.status === 401) {
      localStorage.removeItem('jaritex-auth')
      window.location.href = '/login'
      return Promise.reject(err)
    }

    // 2. Handle Network Errors (no response from server)
    if (!err.response) {
      toast.error(
        'The server is currently unreachable. Please check your connection or try again later.',
        'Network Connection Failed'
      )
    }
    // 3. Handle 500+ Server Errors
    else if (err.response.status >= 500) {
      toast.error(
        'A server-side error occurred. Our team has been notified.',
        'Server Error'
      )
    }
    // 4. Handle Conflict (409) or Bad Request (400) - show backend message
    else if (err.response.status === 409 || err.response.status === 400) {
      const msg = err.response.data?.message || 'The operation could not be completed.'
      const title = err.response.status === 409 ? 'Conflict' : 'Validation Error'
      toast.error(msg, title)
    }
    // 5. Handle other errors (optional, can be done at component level)
    else if (err.response.status === 403) {
      toast.warning('You do not have permission to perform this action.', 'Access Denied')
    }

    return Promise.reject(err)
  }
)

export const authApi = {
  login: (mobile, pin) => api.post('/auth/login', { mobile, pin }),
  me: () => api.get('/auth/me'),
}

export const dashboardApi = {
  adminStats: () => api.get('/dashboard/admin'),
  unitStats: (unitId) => api.get(unitId ? `/dashboard/unit/${unitId}` : '/dashboard/unit'),
  activity: () => api.get('/dashboard/activity'),
  acknowledgeNotification: (id) => api.patch(`/notifications/${id}/acknowledge`),
}

export const cuttingApi = {
  submit: (data) => api.post('/cutting', data),
  listLogs: (params) => api.get('/cutting/logs', { params }),
  updateLog: (id, data) => api.patch(`/cutting/logs/${id}`, data),
  deleteLog: (id) => api.delete(`/cutting/logs/${id}`),
}

export const cuttingBatchApi = {
  create: (logIds, rollsPerBatch) => api.post('/cutting-batches', { logIds, rollsPerBatch }),
  list: (params) => api.get('/cutting-batches', { params }),
  delete: (id) => api.delete(`/cutting-batches/${id}`),
}

export const bobbinApi = {
  availableRolls: (unitId) => api.get(unitId ? `/bobbin/available-rolls/${unitId}` : '/bobbin/available-rolls'),
  createBatch: (data) => api.post('/bobbin', data),
  closeSession: (data) => api.post('/bobbin/close', data),
  list: (params) => api.get('/bobbin', { params }),
}

export const spindleApi = {
  positions: () => api.get('/spindle/positions'),
  scanBatch: (batchId) => api.get(`/spindle/scan-batch/${batchId}`),
  handoff: (data) => api.post('/spindle/loading', data),
  scanOutput: (qr) => api.get(`/spindle/scan-output/${qr}`),
  recordOutput: (data) => api.post('/spindle/output', data),
}

export const boilingApi = {
  confirm: (data) => api.post('/boiling/confirm', data),
  skip: (data) => api.post('/boiling/skip', data),
}

export const coneApi = {
  scanBoiled: (qr) => api.get(`/cone/scan-boiled/${qr}`),
  handoff: (data) => api.post('/cone/handoff', data),
  scanReturn: (qrId) => api.get(`/cone/scan-return/${qrId}`),
  submitReturn: (data) => api.post('/cone/return', data),
}

export const machinesApi = {
  list: () => api.get('/machines'),
  create: (data) => api.post('/machines', data),
  update: (id, data) => api.put(`/machines/${id}`, data),
  addPosition: (machineId, data) => api.post(`/machines/${machineId}/positions`, data),
  updatePosition: (machineId, posId, data) => api.put(`/machines/${machineId}/positions/${posId}`, data),
  listPositionsByUnit: (unitId) => api.get(`/machines/by-unit/${unitId}/positions`),
}

export const operatorsApi = {
  list: (params) => api.get('/operators', { params }),
  create: (data) => api.post('/operators', data),
  update: (id, data) => api.put(`/operators/${id}`, data),
  toggleStatus: (id) => api.patch(`/operators/${id}/toggle`),
  delete: (id) => api.delete(`/operators/${id}`),
}

export const shadesApi = {
  list: () => api.get('/shades'),
  create: (data) => api.post('/shades', data),
  update: (id, data) => api.put(`/shades/${id}`, data),
  delete: (id) => api.delete(`/shades/${id}`),
}

export const containersApi = {
  list: () => api.get('/containers'),
  create: (data) => api.post('/containers', data),
  update: (id, data) => api.put(`/containers/${id}`, data),
  scan: (qr) => api.get(`/containers/scan/${qr}`),
}

export const announcementsApi = {
  list: () => api.get('/announcements'),
  listForUser: () => api.get('/announcements/my'),
  create: (data) => api.post('/announcements', data),
  update: (id, data) => api.put(`/announcements/${id}`, data),
  expire: (id) => api.patch(`/announcements/${id}/expire`),
  reactivate: (id) => api.patch(`/announcements/${id}/reactivate`),
}

export const dispatchApi = {
  list: (params) => api.get('/dispatch', { params }),
  attachCustomer: (id, customerName) => api.patch(`/dispatch/${id}/customer`, { customerName }),
  markDispatched: (id, data) => api.patch(`/dispatch/${id}/dispatch`, data),
}

export const unitsApi = {
  list: () => api.get('/units'),
  create: (data) => api.post('/units', data),
  update: (id, data) => api.put(`/units/${id}`, data),
  delete: (id) => api.delete(`/units/${id}`),
}

export const usersApi = {
  list: () => api.get('/users'),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.put(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
}

export const vendorsApi = {
  list: () => api.get('/vendors'),
  create: (data) => api.post('/vendors', data),
  byId: (id) => api.get(`/vendors/${id}`),
  update: (id, data) => api.put(`/vendors/${id}`, data),
  delete: (id) => api.delete(`/vendors/${id}`),
}

export const customersApi = {
  list: () => api.get('/customers'),
  create: (data) => api.post('/customers', data),
  byId: (id) => api.get(`/customers/${id}`),
  update: (id, data) => api.put(`/customers/${id}`, data),
  delete: (id) => api.delete(`/customers/${id}`),
}

export const procurementApi = {
  listPurchases: (params) => api.get('/procurement', { params }),
  createPurchase: (data) => api.post('/procurement', data),
  finalisePurchase: (id, data) => api.patch(`/procurement/${id}/finalise`, data),
  listRolls: (params) => api.get('/procurement/rolls', { params }),
  rollById: (id) => api.get(`/procurement/rolls/${id}`),
  rollHistory: (id) => api.get(`/procurement/rolls/${id}/history`),
  updateRoll: (id, data) => api.put(`/procurement/rolls/${id}`, data),
  deleteRoll: (id) => api.delete(`/procurement/rolls/${id}`),
}

export const reportsApi = {
  production: (params) => api.get('/reports/production', { params }),
  wastage: (params) => api.get('/reports/wastage', { params }),
  batches: (params) => api.get('/reports/batches', { params }),
  inventory: (params) => api.get('/reports/inventory', { params }),
}

export const costingApi = {
  getConfig: () => api.get('/costing/config'),
  saveConfig: (data) => api.put('/costing/config', data),
  listYarnRates: () => api.get('/costing/yarn-rates'),
  addYarnRate: (data) => api.post('/costing/yarn-rates', data),
  listMetallicRates: () => api.get('/costing/metallic-rates'),
  addMetallicRate: (data) => api.post('/costing/metallic-rates', data),
  batchCost: (batchId) => api.get(`/costing/batch-costs/${batchId}`),
  allBatchCosts: (params) => api.get('/costing/batch-costs', { params }),
  // Bobbin Types
  listBobbinTypes: (includeInactive = false) => api.get('/costing/bobbin-types', { params: { includeInactive } }),
  addBobbinType: (data) => api.post('/costing/bobbin-types', data),
  updateBobbinType: (id, data) => api.put(`/costing/bobbin-types/${id}`, data),
  deleteBobbinType: (id) => api.delete(`/costing/bobbin-types/${id}`),
}

export const notificationsApi = {
  list: (params) => api.get('/notifications', { params }),
  acknowledge: (id) => api.patch(`/notifications/${id}/acknowledge`),
  markAllRead: (data) => api.post('/notifications/mark-all-read', data),
}

export const mmApi = {
  dashboard: (params) => api.get('/mm/dashboard', { params }),
  initiateBatch: (rollId) => api.put(`/mm/rolls/${rollId}/initiate-batch`),
  listInitiatedRolls: (params) => api.get('/mm/initiated-rolls', { params }),
}
